import { AfterViewInit, ChangeDetectionStrategy, Component, effect, inject, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { finalize, map, tap } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { DmiSettingsModel } from '../../../models/dmi-settings.model';
import { DmiStatusModel } from '../../../models/dmi-status.model';
import { OptionalType } from '../../../models/types/optional.type';
import { AlertService } from '../../../services/alert.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { DmiStatusIndicatorComponent } from '../../dmi/dmi-status-indicator/dmi-status-indicator.component';

@Component({
    selector: 'sl-dmi-integration',
    templateUrl: './dmi-integration.component.html',
    styleUrls: ['./dmi-integration.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DmiIntegrationComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('dmiStatusIndicator') dmiStatusIndicator: DmiStatusIndicatorComponent;

    readonly formGroup = new FormGroup({
        dmiVote: new FormControl<boolean>(false),
        dmiServerUrl: new FormControl<string>(''),
        dmiUsername: new FormControl<string>(''),
        dmiPassword: new FormControl<string>(''),
        dmiMeetingId: new FormControl<number>(1),
        dmiTvsUrl: new FormControl<string>('')
    });

    readonly isMakingRequest = signal(false);
    readonly isEditing = signal(false);
    readonly shouldDisableInitReset = signal(false);
    readonly isDmiEnabled: Signal<OptionalType<boolean>>;

    private savedSettings?: DmiSettingsModel;

    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly alertService = inject(AlertService);

    constructor() {
        super();
        this.isDmiEnabled = toSignal(this.formGroup.controls.dmiVote.valueChanges.pipe(map(v => !!v)));

        effect(() => {
            const isEditing = this.isEditing();
            const isDmiEnabled = this.isDmiEnabled();
            if (!isEditing) {
                this.formGroup.disable();
                return;
            }

            this.formGroup.enable();
            this.formGroup.controls['dmiVote'].enable();

            if (isDmiEnabled) {
                this.formGroup.controls['dmiServerUrl'].enable();
                this.formGroup.controls['dmiUsername'].enable();
                this.formGroup.controls['dmiPassword'].enable();
                this.formGroup.controls['dmiMeetingId'].enable();
                this.formGroup.controls['dmiTvsUrl'].enable();
            } else {
                this.formGroup.controls['dmiServerUrl'].disable();
                this.formGroup.controls['dmiUsername'].disable();
                this.formGroup.controls['dmiPassword'].disable();
                this.formGroup.controls['dmiMeetingId'].disable();
                this.formGroup.controls['dmiTvsUrl'].disable();
            }
        });
    }

    ngOnInit(): void {
        this.generalMeetingsResource.getCurrentDmiSettings().subscribe(r => {
            this.savedSettings = r.value;
            this.formGroup.patchValue(r.value);
        });
    }

    ngAfterViewInit(): void {
        const statusSub = this.dmiStatusIndicator.statusChanged.subscribe(newStatus => this.updateInitResetState(newStatus));

        this.updateInitResetState(this.dmiStatusIndicator.dmiStatus);
        this.addSubscriptions(statusSub);
    }

    cancel(): void {
        if (!!this.savedSettings) {
            this.formGroup.setValue(this.savedSettings);
        } else {
            this.formGroup.reset();
        }
        this.isEditing.set(false);
    }

    save(): void {
        this.isMakingRequest.set(true);
        this.generalMeetingsResource
            .saveCurrentDmiSettings(this.formGroup.getRawValue() as DmiSettingsModel)
            .pipe(
                tap(() => this.isEditing.set(false)),
                finalize(() => this.isMakingRequest.set(false))
            )
            .subscribe();
    }

    init(): void {
        this.alertService.showConfirm('DmiSettings.InitConfirm', undefined, () => {
            this.isMakingRequest.set(true);
            this.generalMeetingsResource
                .initDmiServer()
                .pipe(finalize(() => this.isMakingRequest.set(false)))
                .subscribe();
        });
    }

    reset(): void {
        this.alertService.showConfirm('DmiSettings.ResetConfirm', undefined, () => {
            this.isMakingRequest.set(true);
            this.generalMeetingsResource
                .resetDmiServer()
                .pipe(finalize(() => this.isMakingRequest.set(false)))
                .subscribe();
        });
    }

    private updateInitResetState(newStatus?: DmiStatusModel): void {
        this.shouldDisableInitReset.set(!newStatus?.status);
    }
}
